'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { findTranslationKeyAt } from '@/i18n/keys-mode';
import { useKeysMode } from '@/i18n/use-keys-mode';

/**
 * Hover-to-copy for "view translation keys" mode.
 *
 * Keys mode rewrites every `t()` result to its dotted key before render, so
 * what lands in the DOM is a bare text node — there is no per-key component to
 * hang a tooltip on, and wrapping `t()` output in markup would break every
 * caller that expects a string. Instead this mounts once, hit-tests the caret
 * position under the pointer (`caretPositionFromPoint`), asks
 * `findTranslationKeyAt` whether that character sits inside a key, and floats a
 * highlight + copy popover over the token using a Range rect.
 *
 * Everything here is inert unless keys mode is on: no listeners, no DOM.
 * Strings are intentionally untranslated — in keys mode a `t()` label would
 * render as its own key, which is useless on an admin tool.
 */

type HoveredKey = {
  key: string;
  rect: Rect;
};

const POPOVER_ATTR = 'data-keys-mode-popover';
/** Grace period so the pointer can travel from the key to the popover. */
const HIDE_DELAY_MS = 160;
/** Slack around the travel corridor, so a pixel of overshoot doesn't retarget. */
const CORRIDOR_PADDING = 8;

type Rect = { top: number; bottom: number; left: number; right: number };

/**
 * The region the pointer may move through without changing the active key: the
 * key itself, its popover, and the space between them.
 *
 * Without this, aiming for the copy button retargets instead of reaching it —
 * the popover floats over the line above the key, so the pointer crosses that
 * line's own key on the way up and the inspector dutifully switches to it.
 */
function withinCorridor(
  x: number,
  y: number,
  key: Rect,
  popover: Rect | null
): boolean {
  const box = popover ?? key;
  return (
    x >= Math.min(key.left, box.left) - CORRIDOR_PADDING &&
    x <= Math.max(key.right, box.right) + CORRIDOR_PADDING &&
    y >= Math.min(key.top, box.top) - CORRIDOR_PADDING &&
    y <= Math.max(key.bottom, box.bottom) + CORRIDOR_PADDING
  );
}

type CaretHit = { node: Text; offset: number };

function caretAt(x: number, y: number): CaretHit | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number
    ) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };

  const position = doc.caretPositionFromPoint?.(x, y);
  if (position) {
    return position.offsetNode.nodeType === Node.TEXT_NODE
      ? { node: position.offsetNode as Text, offset: position.offset }
      : null;
  }

  // Safari (and older Chromium) only ship the older Range-based spelling.
  const range = doc.caretRangeFromPoint?.(x, y);
  if (range && range.startContainer.nodeType === Node.TEXT_NODE) {
    return { node: range.startContainer as Text, offset: range.startOffset };
  }
  return null;
}

/**
 * Rect of `[start, end)` inside a text node. A wrapped key produces one rect
 * per line, so pick the line the pointer is actually on.
 */
function tokenRect(
  node: Text,
  start: number,
  end: number,
  y: number
): DOMRect | null {
  const range = document.createRange();
  range.setStart(node, start);
  range.setEnd(node, end);

  const rects = Array.from(range.getClientRects());
  const onPointerLine = rects.find((rect) => y >= rect.top && y <= rect.bottom);
  return onPointerLine ?? rects[0] ?? null;
}

async function copyToClipboard(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the legacy path — the clipboard API is gated on a secure
    // context, and non-prod deployments are not always served over HTTPS.
  }

  try {
    const field = document.createElement('textarea');
    field.value = value;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(field);
    return copied;
  } catch {
    return false;
  }
}

export function KeysModeInspector() {
  const keysMode = useKeysMode();
  const [hovered, setHovered] = useState<HoveredKey | null>(null);
  const [copied, setCopied] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frame = useRef<number | null>(null);
  const popover = useRef<HTMLDivElement | null>(null);
  // Read inside the pointermove handler, which the effect below installs once.
  const active = useRef<HoveredKey | null>(null);

  useEffect(() => {
    active.current = hovered;
  }, [hovered]);

  const cancelHide = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    if (hideTimer.current) return;
    hideTimer.current = setTimeout(() => {
      hideTimer.current = null;
      setHovered(null);
      setCopied(false);
    }, HIDE_DELAY_MS);
  }, []);

  useEffect(() => {
    if (!keysMode) return;

    const inspect = (x: number, y: number) => {
      const current = active.current;
      if (
        current &&
        withinCorridor(
          x,
          y,
          current.rect,
          popover.current?.getBoundingClientRect() ?? null
        )
      ) {
        cancelHide();
        return;
      }

      const hit = caretAt(x, y);
      const match = hit && findTranslationKeyAt(hit.node.data, hit.offset);
      if (!hit || !match) {
        scheduleHide();
        return;
      }

      const rect = tokenRect(hit.node, match.start, match.end, y);
      if (!rect) {
        scheduleHide();
        return;
      }

      cancelHide();
      setHovered((previous) =>
        previous &&
        previous.key === match.key &&
        previous.rect.top === rect.top &&
        previous.rect.left === rect.left
          ? previous
          : {
              key: match.key,
              rect: {
                top: rect.top,
                bottom: rect.bottom,
                left: rect.left,
                right: rect.right
              }
            }
      );
    };

    const onPointerMove = (event: PointerEvent) => {
      // Inside the popover the caret hit-test would land on our own markup.
      if (
        event.target instanceof Element &&
        event.target.closest(`[${POPOVER_ATTR}]`)
      ) {
        cancelHide();
        return;
      }

      if (frame.current !== null) cancelAnimationFrame(frame.current);
      const { clientX, clientY } = event;
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        inspect(clientX, clientY);
      });
    };

    // A stale rect is worse than no popover: scrolling moves the key out from
    // under the highlight.
    const dismiss = () => {
      cancelHide();
      setHovered(null);
      setCopied(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };

    document.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('scroll', dismiss, {
      capture: true,
      passive: true
    });
    window.addEventListener('resize', dismiss);
    window.addEventListener('blur', dismiss);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('scroll', dismiss, { capture: true });
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('blur', dismiss);
      document.removeEventListener('keydown', onKeyDown);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      cancelHide();
    };
  }, [keysMode, cancelHide, scheduleHide]);

  // `hovered` is only ever set from a pointer event, so reaching this point
  // guarantees a client render — `document`/`window` below are safe.
  if (!keysMode || !hovered) return null;

  const { key, rect } = hovered;
  // Flip below the token when there is no room above it.
  const above = rect.top > 56;
  const centerX = Math.min(
    Math.max((rect.left + rect.right) / 2, 120),
    window.innerWidth - 120
  );

  const onCopy = async () => {
    const ok = await copyToClipboard(key);
    setCopied(ok);
    if (ok) {
      toast.success(`Copied ${key}`);
    } else {
      toast.error('Could not copy the key');
    }
  };

  return createPortal(
    <>
      <div
        aria-hidden='true'
        className='ring-primary/70 bg-primary/10 pointer-events-none fixed z-[100] rounded-[3px] ring-2'
        style={{
          top: rect.top - 2,
          left: rect.left - 2,
          width: rect.right - rect.left + 4,
          height: rect.bottom - rect.top + 4
        }}
      />
      <div
        ref={popover}
        {...{ [POPOVER_ATTR]: '' }}
        role='dialog'
        aria-label='Translation key'
        onPointerEnter={cancelHide}
        onPointerLeave={scheduleHide}
        className='bg-popover text-popover-foreground fixed z-[101] flex max-w-[min(90vw,28rem)] items-center gap-2 rounded-md border px-2 py-1.5 shadow-md'
        style={{
          top: above ? rect.top - 8 : rect.bottom + 8,
          left: centerX,
          transform: above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)'
        }}
      >
        <code
          dir='ltr'
          className='truncate font-mono text-xs select-all'
          title={key}
        >
          {key}
        </code>
        <button
          type='button'
          onClick={onCopy}
          aria-label={`Copy ${key}`}
          className='hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring inline-flex size-6 shrink-0 items-center justify-center rounded transition-colors focus-visible:ring-2 focus-visible:outline-none'
        >
          {copied ? (
            <Check className='size-3.5 text-green-600' aria-hidden='true' />
          ) : (
            <Copy className='size-3.5' aria-hidden='true' />
          )}
        </button>
      </div>
    </>,
    document.body
  );
}
